import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TYPES = ["Operativo", "Técnico", "Logístico", "Médico", "Seguridad", "Otro"];
const URGENCY = ["Alta", "Media", "Baja"];
const STATUS = ["Abierto", "En atención", "Resuelto"];

const URGENCY_COLOR = {
  Alta: "bg-red-50 text-red-700 border-red-200",
  Media: "bg-amber-50 text-amber-800 border-amber-200",
  Baja: "bg-stone-50 text-stone-700 border-stone-200",
};

export default function StaffIncidents() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: TYPES[0], reporter: "", description: "", urgency: "Media" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/incidents").then(r => setItems(r.data));

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reporter.trim() || !form.description.trim()) {
      toast.error("Completa los campos obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/incidents", form);
      toast.success("Incidencia registrada");
      setForm({ type: TYPES[0], reporter: "", description: "", urgency: "Media" });
      await load();
    } catch {
      toast.error("Error al registrar");
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (inc, status) => {
    setItems(items.map(x => x.id === inc.id ? { ...x, status } : x));
    try { await api.patch(`/incidents/${inc.id}`, { status }); } catch {}
  };

  return (
    <div data-testid="staff-incidents" className="space-y-8">
      <div>
        <div className="overline text-[var(--brand-primary)]">Incidencias</div>
        <h2 className="font-serif-cg text-3xl md:text-4xl tracking-tight mt-2">Registro y atención</h2>
      </div>

      <form onSubmit={submit} data-testid="incident-form" className="card-cg p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="overline text-[var(--brand-muted)]">Tipo de incidencia</label>
          <select
            data-testid="incident-type"
            className="mt-2 w-full bg-white border border-[var(--brand-border)] px-3 py-2.5"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            {TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-[var(--brand-muted)]">Nivel de urgencia</label>
          <select
            data-testid="incident-urgency"
            className="mt-2 w-full bg-white border border-[var(--brand-border)] px-3 py-2.5"
            value={form.urgency}
            onChange={(e) => setForm({ ...form, urgency: e.target.value })}
          >
            {URGENCY.map(u => <option key={u}>{u}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="overline text-[var(--brand-muted)]">Reporta</label>
          <input
            data-testid="incident-reporter"
            className="mt-2 w-full bg-white border border-[var(--brand-border)] px-3 py-2.5"
            value={form.reporter}
            onChange={(e) => setForm({ ...form, reporter: e.target.value })}
            placeholder="Nombre y comité"
          />
        </div>
        <div className="md:col-span-2">
          <label className="overline text-[var(--brand-muted)]">Descripción</label>
          <textarea
            data-testid="incident-description"
            rows={3}
            className="mt-2 w-full bg-white border border-[var(--brand-border)] px-3 py-2.5"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Detalla la situación, hora aproximada y ubicación"
          />
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={submitting} data-testid="incident-submit" className="btn-primary-cg">
            {submitting ? "Registrando..." : "Registrar incidencia"}
          </button>
        </div>
      </form>

      <div>
        <div className="overline text-[var(--brand-muted)] mb-3">Historial de incidencias</div>
        {loading ? (
          <p className="text-[var(--brand-muted)]">Cargando...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[var(--brand-muted)]">Sin incidencias registradas.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((inc, idx) => (
              <li key={inc.id} data-testid={`incident-${idx}`} className="card-cg p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] px-2 py-1 bg-[var(--brand-surface-alt)]">{inc.type}</span>
                  <span className={`text-[11px] px-2 py-1 border ${URGENCY_COLOR[inc.urgency]}`}>
                    Urgencia {inc.urgency}
                  </span>
                  <span className="text-xs text-[var(--brand-muted)] ml-auto">
                    {new Date(inc.time).toLocaleString("es-MX")}
                  </span>
                </div>
                <p className="mt-3"><span className="font-medium">{inc.reporter}:</span> {inc.description}</p>
                <div className="flex gap-2 mt-4">
                  {STATUS.map(s => (
                    <button
                      key={s}
                      onClick={() => changeStatus(inc, s)}
                      data-testid={`incident-${idx}-status-${s}`}
                      className={`text-[11px] px-2.5 py-1 border ${
                        inc.status === s
                          ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                          : "border-[var(--brand-border)] hover:border-[var(--brand-primary)]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
